// src/settingComponent/apiSetting/ApiGlobalSettings.jsx
import React, { useState, useEffect, createContext } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  collection,
  getDocs
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';

import ApiKeySection from './ApiKeySection';
import ApiBilling from './apiBilling/ApiBilling';

export const ApiEnabledContext = createContext(false);

const ApiGlobalSettings = () => {
  const user = auth.currentUser;
  const functions = getFunctions();
  const enableAPICallable = httpsCallable(functions, 'enableAPI');

  // localStorage keys
  const apigeeStorageKey = user ? `apigeeDetails_${user.uid}` : null;
  const keysStorageKey   = user ? `apiKeys_${user.uid}` : null;

  // state
  const [isApiEnabled, setIsApiEnabled]   = useState(false);
  const [isEnabling, setIsEnabling]       = useState(false);
  const [error, setError]                 = useState(null);
  const [apiProducts, setApiProducts]     = useState([]);
  const [apiKeys, setApiKeys]             = useState([]);
  const [creditDetails, setCreditDetails] = useState(null);
  const [invoices, setInvoices]           = useState([]); // New state for invoices

  // ─── apiEnabled (with cache + realtime) ─────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const apigeeRef = doc(db, 'users', user.uid, 'api', 'apigeeDetails');

    // hydrate from cache
    const cached = localStorage.getItem(apigeeStorageKey);
    if (cached) {
      setIsApiEnabled(JSON.parse(cached).apiEnabled);
    } else {
      getDoc(apigeeRef).then(snap => {
        const enabled = snap.exists() && !!snap.data().apiEnabled;
        setIsApiEnabled(enabled);
        localStorage.setItem(
          apigeeStorageKey,
          JSON.stringify({ apiEnabled: enabled })
        );
      });
    }

    // realtime listener
    const unSub = onSnapshot(
      apigeeRef,
      snap => {
        const enabled = snap.exists() && !!snap.data().apiEnabled;
        setIsApiEnabled(enabled);
        localStorage.setItem(
          apigeeStorageKey,
          JSON.stringify({ apiEnabled: enabled })
        );
      },
      // err => console.error('apiEnabled onSnapshot error', err)
    );

    return () => unSub();
  }, [user, apigeeStorageKey]);

  // ─── apiProducts ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'apiProducts'));
        setApiProducts(snap.docs.map(d => d.data()));
      } catch (err) {
        // console.error('Error loading apiProducts:', err);
      }
    })();
  }, []);

  // ─── apiKeys (cache + realtime) ───────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const keysRef = collection(db, 'users', user.uid, 'apiKeys');

    // hydrate from cache
    const cached = localStorage.getItem(keysStorageKey);
    if (cached) {
      try {
        setApiKeys(JSON.parse(cached));
      } catch {}
    }

    // realtime listener
    const unSub = onSnapshot(
      keysRef,
      snap => {
        const keys = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setApiKeys(keys);
        localStorage.setItem(keysStorageKey, JSON.stringify(keys));
      },
      // err => console.error('apiKeys onSnapshot error', err)
    );

    return () => unSub();
  }, [user, keysStorageKey]);

  // ─── creditDetails (realtime) ────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const creditRef = doc(db, 'users', user.uid, 'api', 'creditDetails');

    const unSub = onSnapshot(
      creditRef,
      snap => {
        if (snap.exists()) {
          setCreditDetails(snap.data());
        }
      },
      // err => console.error('creditDetails onSnapshot error', err)
    );

    return () => unSub();
  }, [user]);

  // ─── invoices (realtime) ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const invoicesRef = collection(db, 'users', user.uid, 'apiInvoices');

    const unSub = onSnapshot(
      invoicesRef,
      snap => {
        const fetchedInvoices = snap.docs.map(d => ({
          id: d.id,
          ...d.data(),
        }));
        setInvoices(fetchedInvoices);
      },
      // err => console.error('invoices onSnapshot error', err)
    );

    return () => unSub();
  }, [user]);

  // ─── handleEnableAPI ─────────────────────────────────────────────────────
  const handleEnableAPI = async () => {
    setIsEnabling(true);
    setError(null);

    try {
      await enableAPICallable({});
      const apigeeRef = doc(db, 'users', user.uid, 'api', 'apigeeDetails');
      await setDoc(apigeeRef, { apiEnabled: true }, { merge: true });
      setIsApiEnabled(true);
      localStorage.setItem(
        apigeeStorageKey,
        JSON.stringify({ apiEnabled: true })
      );
    } catch (err) {
      setError('Failed to enable API. Please retry in a couple minutes.');
      // console.error(err);
    } finally {
      setIsEnabling(false);
    }
  };

  return (
    <ApiEnabledContext.Provider value={isApiEnabled}>
      <div className="set-account-settings-wrapper">
        <ApiKeySection
          isApiEnabled={isApiEnabled}
          isEnabling={isEnabling}
          onEnableAPI={handleEnableAPI}
          error={error}
          products={apiProducts}
          apiKeys={apiKeys}
        />
        <ApiBilling creditDetails={creditDetails} invoices={invoices} /> {/* Pass invoices */}
      </div>
    </ApiEnabledContext.Provider>
  );
};

export default ApiGlobalSettings;
